// Compare mode (PLAN §7.1, task 20a): the same document extracted on 2–3 routes side by side. `compareRuns` lines
// the runs up as a matrix (one row per extracted item, one cell per route: found / missing / differs) with agreement
// stats; `planAdoption` turns per-item picks into the candidates to keep. Pure: the pipeline computes the stats when
// the runs are staged, the app recomputes the matrix for review and applies the picks. Nothing here reads content
// as instructions: names and values are only compared, normalized, and shown.
import { normalizeName } from './names';

export const COMPARE_ROUTES = ['anthropic', 'openai', 'native'] as const;
export type CompareRoute = (typeof COMPARE_ROUTES)[number];
export const isCompareRoute = (v: unknown): v is CompareRoute => COMPARE_ROUTES.includes(v as CompareRoute);

/** How routes are named everywhere people see them. */
export const ROUTE_LABELS: Record<CompareRoute, string> = { anthropic: 'Claude', openai: 'OpenAI', native: 'Native' };

/** Prefix that keeps the refs of each compared run apart in one document's candidates (`openai:E3`). */
export const compareRef = (route: CompareRoute, ref: string) => `${route}:${ref}`;

export interface CompareEntityIn {
  /** Candidate id (or the ref, before candidates exist). */
  id: string;
  ref: string;
  kind: string;
  name: string;
  /** Every name the run gave it (name, aliases, as written). */
  names: string[];
  type: string | null;
}

export interface CompareClaimIn {
  id: string;
  ref: string;
  shape: string;
  subject: string;
  object: string | null;
  place: string | null;
  source: string | null;
  predicate: string;
  value: string | null;
  date: string | null;
  endDate: string | null;
  amount: string | null;
  text: string;
}

export interface CompareRunIn {
  route: CompareRoute;
  entities: CompareEntityIn[];
  claims: CompareClaimIn[];
}

export type CompareCellStatus = 'found' | 'missing' | 'differs';
export type CompareAgreement = 'all' | 'partial' | 'unique';

export interface CompareCell {
  status: CompareCellStatus;
  id?: string;
  ref?: string;
  /** Entity name, or the claim's text. */
  label?: string;
  values?: Record<string, string | null>;
  /** Fields where this cell disagrees with the others. */
  diff?: string[];
}

export interface CompareRow {
  /** Stable for the same runs: per-item picks are keyed by it. */
  key: string;
  type: 'entity' | 'claim';
  kind: string;
  label: string;
  agreement: CompareAgreement;
  uniqueTo: CompareRoute | null;
  cells: Partial<Record<CompareRoute, CompareCell>>;
  /** Fields the routes disagree on (names, types, dates, relation types, amounts, values). */
  differences: string[];
}

export interface RouteAgreement {
  found: number;
  missing: number;
  differs: number;
}

export interface CompareStats {
  routes: CompareRoute[];
  items: number;
  /** Found by every route, with the same values. */
  allAgree: number;
  /** Found by more than one route but not all, or with different values. */
  partial: number;
  /** Found by one route only, per route. */
  unique: Partial<Record<CompareRoute, number>>;
  byRoute: Partial<Record<CompareRoute, RouteAgreement>>;
  /** allAgree / items (1 when there is nothing to compare). */
  agreementRate: number;
}

/** Which fields are compared, and what they are called on screen. */
export const COMPARE_FIELD_LABELS: Record<string, string> = {
  name: 'name',
  type: 'type',
  predicate: 'relation type',
  value: 'value',
  date: 'date',
  endDate: 'end date',
  amount: 'amount',
};

const norm = (v: string | null | undefined) => (v ? normalizeName(v) : '');

interface EntityRow {
  key: string;
  kind: string;
  names: Set<string>;
  cells: Map<CompareRoute, CompareEntityIn>;
}
interface ClaimRow {
  key: string;
  shape: string;
  sig: string;
  cells: Map<CompareRoute, CompareClaimIn>;
}

const uniqueKey = (base: string, used: Set<string>) => {
  let key = base;
  for (let n = 2; used.has(key); n++) key = `${base}#${n}`;
  used.add(key);
  return key;
};

/** Lines the entities of every run up: same kind and a shared name (normalized) → the same row. */
function entityRows(runs: CompareRunIn[], used: Set<string>): { rows: EntityRow[]; rowOf: Map<string, string> } {
  const rows: EntityRow[] = [];
  const rowOf = new Map<string, string>();
  for (const run of runs)
    for (const e of run.entities) {
      const names = new Set([e.name, ...e.names].map(norm).filter(Boolean));
      let row = rows.find(r => r.kind === e.kind && !r.cells.has(run.route) && [...names].some(n => r.names.has(n)));
      if (!row) {
        row = { key: uniqueKey(`e:${e.kind}:${norm(e.name) || e.ref}`, used), kind: e.kind, names: new Set(), cells: new Map() };
        rows.push(row);
      }
      for (const n of names) row.names.add(n);
      row.cells.set(run.route, e);
      rowOf.set(`${run.route}|${e.ref}`, row.key);
    }
  return { rows, rowOf };
}

/** Claims line up by shape, subject row and object row (relations) or attribute name (facts about one entity). */
function claimRows(runs: CompareRunIn[], rowOf: Map<string, string>, used: Set<string>): ClaimRow[] {
  const rows: ClaimRow[] = [];
  for (const run of runs)
    for (const c of run.claims) {
      const subject = rowOf.get(`${run.route}|${c.subject}`) ?? `?${c.subject}`;
      const object = c.object ? (rowOf.get(`${run.route}|${c.object}`) ?? `?${c.object}`) : `=${norm(c.predicate)}`;
      const sig = `${c.shape}|${subject}|${object}`;
      let row = rows.find(r => r.sig === sig && !r.cells.has(run.route));
      if (!row) {
        row = { key: uniqueKey(`c:${sig}`, used), shape: c.shape, sig, cells: new Map() };
        rows.push(row);
      }
      row.cells.set(run.route, c);
    }
  return rows;
}

/**
 * Marks the cells that disagree: for each field, the value most routes gave is the reference; a cell with another
 * value differs. With no majority (two routes, two values; three routes, three values) every found cell differs.
 */
function diffCells(values: Map<CompareRoute, Record<string, string | null>>): Map<CompareRoute, string[]> {
  const out = new Map<CompareRoute, string[]>([...values.keys()].map(r => [r, []]));
  if (values.size < 2) return out;
  const fields = new Set([...values.values()].flatMap(v => Object.keys(v)));
  for (const f of fields) {
    const counts = new Map<string, number>();
    for (const v of values.values()) counts.set(norm(v[f]), (counts.get(norm(v[f])) ?? 0) + 1);
    if (counts.size < 2) continue;
    const top = Math.max(...counts.values());
    const majority = [...counts.entries()].filter(([, n]) => n === top);
    const ref = majority.length === 1 ? majority[0]?.[0] : undefined;
    for (const [route, v] of values) if (ref === undefined || norm(v[f]) !== ref) out.get(route)?.push(f);
  }
  return out;
}

const entityValues = (e: CompareEntityIn) => ({ name: e.name, type: e.type });
const claimValues = (c: CompareClaimIn) => ({
  predicate: c.predicate,
  value: c.value,
  date: c.date,
  endDate: c.endDate,
  amount: c.amount,
});

/** The comparison matrix and its agreement stats. Only the runs given (the ones that did not fail) are columns. */
export function compareRuns(runs: CompareRunIn[]): { rows: CompareRow[]; stats: CompareStats } {
  const routes = runs.map(r => r.route);
  const used = new Set<string>();
  const ents = entityRows(runs, used);
  const claims = claimRows(runs, ents.rowOf, used);
  const nameOfRow = new Map(ents.rows.map(r => [r.key, [...r.cells.values()][0]?.name ?? '']));

  const build = <T extends CompareEntityIn | CompareClaimIn>(
    type: 'entity' | 'claim',
    key: string,
    kind: string,
    cells: Map<CompareRoute, T>,
    valuesOf: (x: T) => Record<string, string | null>,
    labelOf: (x: T) => string,
  ): CompareRow => {
    const values = new Map([...cells.entries()].map(([r, x]) => [r, valuesOf(x)]));
    const diffs = diffCells(values);
    const out: CompareRow['cells'] = {};
    for (const route of routes) {
      const x = cells.get(route);
      const diff = diffs.get(route) ?? [];
      out[route] = x
        ? { status: diff.length ? 'differs' : 'found', id: x.id, ref: x.ref, label: labelOf(x), values: valuesOf(x), diff }
        : { status: 'missing' };
    }
    const differences = [...new Set([...diffs.values()].flat())];
    const foundBy = routes.filter(r => cells.has(r));
    const agreement: CompareAgreement =
      foundBy.length === 1 && routes.length > 1 ? 'unique' : foundBy.length === routes.length && !differences.length ? 'all' : 'partial';
    const first = [...cells.values()][0] as T;
    return {
      key,
      type,
      kind,
      label: labelOf(first),
      agreement,
      uniqueTo: agreement === 'unique' ? (foundBy[0] ?? null) : null,
      cells: out,
      differences,
    };
  };

  const rows: CompareRow[] = [
    ...ents.rows.map(r => build('entity', r.key, r.kind, r.cells, entityValues, e => e.name)),
    ...claims.map(r => {
      const subject = nameOfRow.get(r.sig.split('|')[1] ?? '') ?? '';
      return build(
        'claim',
        r.key,
        r.shape,
        r.cells,
        claimValues,
        c => c.text || [subject, c.predicate, c.value].filter(Boolean).join(' · '),
      );
    }),
  ];
  // Most disagreement first (items one route missed or read differently), entities before claims, then as found.
  const score = (r: CompareRow) => Object.values(r.cells).filter(c => c.status !== 'found').length;
  const order = new Map(rows.map((r, i) => [r.key, i]));
  rows.sort((a, b) => score(b) - score(a) || (order.get(a.key) ?? 0) - (order.get(b.key) ?? 0));

  const byRoute: CompareStats['byRoute'] = {};
  const unique: CompareStats['unique'] = {};
  for (const route of routes) {
    const cells = rows.map(r => r.cells[route]);
    byRoute[route] = {
      found: cells.filter(c => c?.status === 'found').length,
      missing: cells.filter(c => c?.status === 'missing').length,
      differs: cells.filter(c => c?.status === 'differs').length,
    };
    unique[route] = rows.filter(r => r.uniqueTo === route).length;
  }
  const allAgree = rows.filter(r => r.agreement === 'all').length;
  return {
    rows,
    stats: {
      routes,
      items: rows.length,
      allAgree,
      partial: rows.filter(r => r.agreement === 'partial').length,
      unique,
      byRoute,
      agreementRate: rows.length ? allAgree / rows.length : 1,
    },
  };
}

/** Picks per item: the route whose version is kept, or null to leave the item out. */
export type ComparePicks = Record<string, CompareRoute | null>;

export interface AdoptionPlan {
  /** Candidate ids kept (entities and claims). */
  keep: string[];
  /** Claims kept with their entity refs pointed at the picked version of those entities (another route's ref). */
  rewrites: { id: string; subject: string; object: string | null; place: string | null; source: string | null }[];
  /** Claims left out because an entity they need was left out. */
  dropped: string[];
  /** Items kept per route. */
  byRoute: Partial<Record<CompareRoute, number>>;
}

/**
 * Per-item adoption: keeps the picked cell of each row. A claim picked from one route whose entities were picked
 * from another is re-pointed at those; a claim whose subject or object was left out is dropped (a place or cited
 * source left out is simply cleared). Refs are the run-prefixed candidate refs (`compareRef`).
 */
export function planAdoption(runs: CompareRunIn[], rows: CompareRow[], picks: ComparePicks): AdoptionPlan {
  const rowKeyOf = new Map<string, string>();
  for (const row of rows)
    for (const [route, cell] of Object.entries(row.cells) as [CompareRoute, CompareCell][])
      if (cell.ref && row.type === 'entity') rowKeyOf.set(`${route}|${cell.ref}`, row.key);
  const pickedRef = (route: CompareRoute, ref: string | null): string | null | undefined => {
    if (!ref) return null;
    const key = rowKeyOf.get(`${route}|${ref}`);
    if (!key) return undefined;
    const pick = picks[key];
    if (!pick) return undefined;
    return rows.find(r => r.key === key)?.cells[pick]?.ref ?? undefined;
  };
  const keep: string[] = [];
  const rewrites: AdoptionPlan['rewrites'] = [];
  const dropped: string[] = [];
  const byRoute: AdoptionPlan['byRoute'] = {};
  for (const row of rows) {
    const pick = picks[row.key];
    const cell = pick ? row.cells[pick] : undefined;
    if (!pick || !cell?.id) continue;
    if (row.type === 'entity') {
      keep.push(cell.id);
      byRoute[pick] = (byRoute[pick] ?? 0) + 1;
      continue;
    }
    const claim = runs.find(r => r.route === pick)?.claims.find(c => c.id === cell.id);
    if (!claim) continue;
    const subject = pickedRef(pick, claim.subject);
    const object = pickedRef(pick, claim.object);
    if (!subject || object === undefined) {
      dropped.push(claim.id);
      continue;
    }
    const place = pickedRef(pick, claim.place) ?? null;
    const source = pickedRef(pick, claim.source) ?? null;
    keep.push(claim.id);
    byRoute[pick] = (byRoute[pick] ?? 0) + 1;
    if (subject !== claim.subject || object !== claim.object || place !== claim.place || source !== claim.source)
      rewrites.push({ id: claim.id, subject, object, place, source });
  }
  return { keep, rewrites, dropped, byRoute };
}

/** Default picks: every item from `route` where it found it, else from the first route that found it. */
export function defaultPicks(rows: CompareRow[], route: CompareRoute): ComparePicks {
  const out: ComparePicks = {};
  for (const r of rows) {
    const first = (Object.entries(r.cells) as [CompareRoute, CompareCell][]).find(([, c]) => c.status !== 'missing')?.[0] ?? null;
    out[r.key] = r.cells[route]?.status !== 'missing' && r.cells[route] ? route : first;
  }
  return out;
}

/** What the compare step shows about each run (status, counts, model, time, cost). */
export interface CompareRunSummary {
  runId: string;
  route: CompareRoute;
  label: string;
  model: string | null;
  status: 'ok' | 'partial' | 'failed';
  entities: number;
  claims: number;
  costUsd: number;
  latencyMs: number;
  adopted: boolean;
}

/** Who adopted what, stored on the comparison. */
export interface ComparePicksRecord {
  mode: 'document' | 'items';
  byRoute: Partial<Record<CompareRoute, number>>;
  /** Per item (items mode): row key → route or null (left out). */
  items?: ComparePicks;
  dropped?: number;
}
